import { useState, useEffect, useRef, useCallback } from "react";
import "@fontsource/playfair-display/400.css";
import "@fontsource/playfair-display/700.css";
import "@fontsource/playfair-display/900.css";
import "@fontsource/outfit/300.css";
import "@fontsource/outfit/400.css";
import "@fontsource/outfit/600.css";
import { Play, Mail, Share2, Heart, Twitter, Facebook, ChevronDown, ExternalLink, List, X } from "lucide-react";
import axios from "axios";
import Hls from "hls.js";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VENMO_USERNAME = "lostsoulwife";

/* ─── HLS-capable <video> wrapper ──────────────────────────────
   Emergent Object Storage doesn't support HTTP Range, and Cloudflare
   kills single responses > ~100MB. HLS solves both by serving small
   .ts segments (~3-5MB each). Safari plays .m3u8 natively; everything
   else uses hls.js. */
const HlsVideo = ({ videoId, className, testId, videoRef }) => {
  const innerRef = useRef(null);
  const [available, setAvailable] = useState(null); // null=checking, true/false
  const src = `${API}/hls/${videoId}/master.m3u8`;

  // Probe availability before mounting the player (avoids noisy 404s)
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/hls/${videoId}`)
      .then(r => { if (!cancelled) setAvailable(!!r.data?.available); })
      .catch(() => { if (!cancelled) setAvailable(false); });
    return () => { cancelled = true; };
  }, [videoId]);

  useEffect(() => {
    if (!available) return;
    const video = innerRef.current;
    if (!video) return;
    if (videoRef) videoRef.current = video;

    // Safari / iOS have native HLS support
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      return;
    }
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(src);
      hls.attachMedia(video);
      return () => { try { hls.destroy(); } catch {} };
    }
    video.src = src;
  }, [src, videoRef, available]);

  if (available === false) {
    return (
      <div className={`${className} flex flex-col items-center justify-center bg-black text-[#A0A0A0]`} data-testid={`${testId}-placeholder`}>
        <Play className="w-10 h-10 mb-3 text-[#E5C07B]/40" />
        <p className="text-xs tracking-[0.3em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>Trailer Coming Soon</p>
      </div>
    );
  }

  return (
    <video
      ref={innerRef}
      data-testid={testId}
      className={className}
      controls
      controlsList="nodownload"
      playsInline
      preload="metadata"
    />
  );
};

/* ─── Ember Particles Canvas ─── */
const EmberParticles = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let particles = [];
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    class Ember {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = canvas.height + 10;
        this.size = Math.random() * 3 + 1;
        this.speedY = -(Math.random() * 1.5 + 0.3);
        this.speedX = (Math.random() - 0.5) * 0.8;
        this.opacity = Math.random() * 0.8 + 0.2;
        this.decay = Math.random() * 0.003 + 0.001;
        this.color = Math.random() > 0.5 ? "#E5C07B" : "#FF3D00";
      }
      update() {
        this.y += this.speedY;
        this.x += this.speedX + Math.sin(this.y * 0.01) * 0.3;
        this.opacity -= this.decay;
        if (this.opacity <= 0 || this.y < -10) this.reset();
      }
      draw() {
        ctx.save(); ctx.globalAlpha = this.opacity; ctx.fillStyle = this.color;
        ctx.shadowColor = this.color; ctx.shadowBlur = this.size * 4;
        ctx.beginPath(); ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2); ctx.fill(); ctx.restore();
      }
    }
    for (let i = 0; i < 60; i++) { const e = new Ember(); e.y = Math.random() * canvas.height; particles.push(e); }
    const animate = () => { ctx.clearRect(0, 0, canvas.width, canvas.height); particles.forEach((p) => { p.update(); p.draw(); }); animId = requestAnimationFrame(animate); };
    animate();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none" style={{ zIndex: 5 }} />;
};

/* ─── Ad Zone (AdSense Active) ─── */
const AdZone = ({ slot, className = "" }) => (
  <div data-testid={`ad-zone-${slot}`} className={`w-full rounded-sm min-h-[90px] ${className}`}>
    <ins className="adsbygoogle" style={{ display: "block" }} data-ad-client="ca-pub-9932397889507298" data-ad-slot={slot} data-ad-format="auto" data-full-width-responsive="true" />
  </div>
);

/* ─── Chapter Selection Panel ─── */
const ChapterMenu = ({ chapters, videoRef, onClose }) => {
  const seekTo = (timestamp) => {
    if (videoRef?.current) {
      videoRef.current.currentTime = timestamp;
      videoRef.current.play().catch(() => {});
      onClose();
    }
  };

  return (
    <div data-testid="chapter-menu-overlay" className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,5,5,0.92)", backdropFilter: "blur(12px)" }}>
      <div className="w-full max-w-5xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[10px] tracking-[0.4em] uppercase text-[#E5C07B] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Scene Selection</p>
            <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif", background: "linear-gradient(180deg, #F5E6C4, #C99C3F)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Chapters
            </h2>
          </div>
          <button data-testid="chapter-menu-close" onClick={onClose} className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all">
            <X className="w-5 h-5 text-[#A0A0A0]" />
          </button>
        </div>

        {/* Chapter Grid */}
        <div className="overflow-y-auto pr-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" style={{ scrollbarWidth: "thin", scrollbarColor: "#333 transparent" }}>
          {chapters.map((ch, idx) => (
            <button
              key={ch.chapter_number}
              data-testid={`chapter-select-${ch.chapter_number}`}
              onClick={() => seekTo(ch.timestamp)}
              className="group text-left bg-[#0A0A0A] border border-white/8 rounded-lg overflow-hidden hover:border-[#E5C07B]/40 transition-all"
            >
              {/* Thumbnail */}
              <div className="aspect-video bg-[#111] relative overflow-hidden">
                {ch.thumbnail_id && (
                  <img
                    src={`${API}/chapters/${ch.thumbnail_id}/thumbnail`}
                    alt={ch.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-1 right-1 bg-black/70 text-[#E5C07B] text-[9px] px-1.5 py-0.5 rounded font-mono" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {ch.timestamp_display}
                </div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-8 h-8 rounded-full bg-[#E5C07B]/20 flex items-center justify-center backdrop-blur-sm">
                    <Play className="w-4 h-4 text-[#E5C07B] fill-current ml-0.5" />
                  </div>
                </div>
              </div>
              {/* Info */}
              <div className="p-2.5">
                <p className="text-[9px] text-[#E5C07B] tracking-widest uppercase mb-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {ch.chapter_number === 55 ? "Ch 5.5" : `Ch ${ch.chapter_number}`}
                </p>
                <p className="text-xs text-[#D0D0D0] leading-tight line-clamp-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {ch.title}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Metallic Gold Title ─── */
const MetallicTitle = () => (
  <h1 data-testid="movie-title" className="text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-black tracking-tighter leading-none mb-2" style={{
    fontFamily: "'Playfair Display', serif",
    background: "linear-gradient(180deg, #FFF8E7 0%, #F5D98C 20%, #C99C3F 45%, #A67C28 55%, #D4AA4F 70%, #F5E6C4 85%, #C99C3F 100%)",
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
    filter: "drop-shadow(0 2px 8px rgba(201,156,63,0.4)) drop-shadow(0 0 40px rgba(229,192,123,0.2))",
    backgroundSize: "100% 200%", animation: "metallicShimmer 6s ease-in-out infinite",
  }}>The UnMaking</h1>
);

/* ─── Movie Page ─── */
export default function MoviePage() {
  const [showPlayer, setShowPlayer] = useState(false);
  const [showChapterMenu, setShowChapterMenu] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState("idle");
  const [copied, setCopied] = useState(false);
  const playerRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/chapters/timeline`).then(r => setChapters(r.data.chapters || [])).catch(() => {});
  }, []);

  const scrollToPlayer = useCallback(() => {
    setShowPlayer(true);
    setTimeout(() => {
      playerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => { videoRef.current?.play().catch(() => {}); }, 800);
    }, 100);
  }, []);

  const openChapterMenu = () => {
    if (!showPlayer) setShowPlayer(true);
    setShowChapterMenu(true);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setEmailStatus("loading");
    try { await axios.post(`${API}/subscribe`, { email: email.trim() }); setEmailStatus("success"); setEmail(""); }
    catch { setEmailStatus("error"); setTimeout(() => setEmailStatus("idle"), 3000); }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: "The UnMaking", text: "Watch The UnMaking — A Dark Fantasy Epic", url }); } catch {} }
    else { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5E6C4] overflow-x-hidden" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <style>{`
        @keyframes metallicShimmer { 0%, 100% { background-position: 0% 0%; } 50% { background-position: 0% 100%; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .fade-in-up { animation: fadeInUp 1s ease-out forwards; }
        .fade-in-up-d1 { animation: fadeInUp 1s ease-out 0.2s forwards; opacity: 0; }
        .fade-in-up-d2 { animation: fadeInUp 1s ease-out 0.4s forwards; opacity: 0; }
        .fade-in-up-d3 { animation: fadeInUp 1s ease-out 0.6s forwards; opacity: 0; }
        .fade-in-up-d4 { animation: fadeInUp 1s ease-out 0.8s forwards; opacity: 0; }
      `}</style>

      <EmberParticles />
      {showChapterMenu && <ChapterMenu chapters={chapters} videoRef={videoRef} onClose={() => setShowChapterMenu(false)} />}

      {/* ── HERO ── */}
      <section data-testid="hero-section" className="relative min-h-screen flex flex-col items-center justify-center text-center px-6">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
          <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#FF3D00]/5 to-transparent" />
        </div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[2px] h-40 bg-gradient-to-t from-[#FF3D00] via-[#FF3D00]/40 to-transparent" style={{ filter: "drop-shadow(0 0 12px rgba(255,61,0,0.8))" }} />

        <div className="relative z-10 max-w-4xl">
          <p className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#A0A0A0] mb-8 fade-in-up" style={{ fontFamily: "'Outfit', sans-serif" }}>A Christopher Marshall Film</p>
          <div className="fade-in-up-d1"><MetallicTitle /></div>
          <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-[#E5C07B]/60 to-transparent mx-auto my-8 fade-in-up-d2" />
          <p className="text-base sm:text-lg text-[#A0A0A0] max-w-2xl mx-auto mb-12 leading-relaxed fade-in-up-d2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            When heaven declares war and ancient seals shatter, one man must unite fallen angels and awakened titans to prevent the unmaking of existence itself.
          </p>

          <div className="flex items-center justify-center gap-4 fade-in-up-d3">
            <button data-testid="watch-now-button" onClick={scrollToPlayer}
              className="group inline-flex items-center gap-4 font-bold rounded-sm px-10 py-5 transition-all uppercase tracking-[0.2em] text-sm"
              style={{ fontFamily: "'Outfit', sans-serif", background: "linear-gradient(135deg, #E5C07B, #C99C3F)", color: "#050505", boxShadow: "0 0 40px rgba(229,192,123,0.3), inset 0 1px 0 rgba(255,255,255,0.2)" }}>
              <Play className="w-5 h-5 fill-current" /> Watch Now
            </button>
            <button data-testid="chapters-button" onClick={openChapterMenu}
              className="inline-flex items-center gap-3 font-bold rounded-sm px-6 py-5 transition-all uppercase tracking-[0.2em] text-sm border border-[#E5C07B]/30 text-[#E5C07B] hover:bg-[#E5C07B]/10"
              style={{ fontFamily: "'Outfit', sans-serif" }}>
              <List className="w-5 h-5" /> Chapters
            </button>
          </div>

          <div className="mt-6 flex items-center justify-center gap-6 text-[#666] text-xs tracking-widest uppercase fade-in-up-d4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <span>3 Hours 7 Minutes</span><span className="w-1 h-1 bg-[#666] rounded-full" /><span>Prologue + 35 Chapters</span><span className="w-1 h-1 bg-[#666] rounded-full" /><span>Dark Fantasy</span>
          </div>
        </div>
        <div className="absolute bottom-8 z-10 animate-bounce"><ChevronDown className="w-6 h-6 text-[#666]" /></div>
      </section>

      {/* ── PRE-ROLL AD ── */}
      <div className="max-w-5xl mx-auto px-6 py-4 relative z-10"><AdZone slot="pre-roll" /></div>

      {/* ── VIDEO PLAYER ── */}
      <section ref={playerRef} data-testid="player-section" className="relative py-16 px-6 z-10">
        <div className="max-w-5xl mx-auto">
          {!showPlayer ? (
            <div data-testid="player-placeholder" onClick={scrollToPlayer}
              className="aspect-video bg-black rounded-lg border border-white/10 flex flex-col items-center justify-center cursor-pointer group transition-all hover:border-[#E5C07B]/30"
              style={{ boxShadow: "0 0 60px rgba(255,61,0,0.1)" }}>
              <div className="w-20 h-20 rounded-full bg-[#E5C07B]/10 flex items-center justify-center mb-4 group-hover:bg-[#E5C07B]/20 transition-all" style={{ boxShadow: "0 0 30px rgba(229,192,123,0.2)" }}>
                <Play className="w-8 h-8 text-[#E5C07B] fill-current ml-1" />
              </div>
              <p className="text-[#A0A0A0] text-sm tracking-widest uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>Click to Play</p>
            </div>
          ) : (
            <div className="relative">
              <div className="aspect-video bg-black rounded-lg border border-white/10 overflow-hidden" style={{ boxShadow: "0 0 60px rgba(255,61,0,0.15)" }}>
                <HlsVideo videoId="THE_UNMAKING_FULL_MOVIE" testId="movie-player" className="w-full h-full" videoRef={videoRef} />
              </div>
              {/* Chapter menu button below player */}
              <div className="flex items-center justify-center mt-4">
                <button data-testid="chapters-button-player" onClick={() => setShowChapterMenu(true)}
                  className="inline-flex items-center gap-2 text-[#A0A0A0] text-xs tracking-widest uppercase hover:text-[#E5C07B] transition-all px-4 py-2 border border-white/10 rounded-full hover:border-[#E5C07B]/30"
                  style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <List className="w-4 h-4" /> Chapter Selection
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── BANNER AD ── */}
      <div className="max-w-5xl mx-auto px-6 py-4 relative z-10"><AdZone slot="mid-banner" /></div>

      {/* ── BOOK II — FULL MOVIE ── */}
      <section data-testid="book2-full-section" className="relative py-20 px-6 z-10 border-t border-b border-[#E5C07B]/10" style={{ background: "linear-gradient(180deg, transparent, rgba(255,61,0,0.04), transparent)" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] tracking-[0.4em] uppercase text-[#FF3D00] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Now Streaming</p>
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif", background: "linear-gradient(180deg, #F5E6C4, #C99C3F)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Book II — Full Movie
            </h2>
            <div className="w-16 h-[1px] bg-gradient-to-r from-transparent via-[#E5C07B]/40 to-transparent mx-auto mb-5" />
            <p className="text-[#A0A0A0] text-sm sm:text-base max-w-2xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Four bearers. One arbiter. The system breaks — and something from outside it answers.
            </p>
          </div>

          <div className="aspect-video bg-black rounded-lg border border-[#E5C07B]/20 overflow-hidden" style={{ boxShadow: "0 0 80px rgba(255,61,0,0.15)" }}>
            <HlsVideo videoId="BOOK2_FULL" testId="book2-full-player" className="w-full h-full" />
          </div>

          <p className="text-center text-[#A0A0A0]/60 text-xs tracking-widest uppercase mt-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Prologue + Chapters 1–35 · Full feature presentation
          </p>
        </div>
      </section>

      {/* ── SYNOPSIS ── */}
      <section data-testid="synopsis-section" className="relative py-24 px-6 z-10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#E5C07B] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>The Story</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-8" style={{ fontFamily: "'Playfair Display', serif", background: "linear-gradient(180deg, #F5E6C4, #C99C3F)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            A War Older Than Creation
          </h2>
          <div className="w-16 h-[1px] bg-gradient-to-r from-transparent via-[#E5C07B]/40 to-transparent mx-auto mb-8" />
          <p className="text-[#A0A0A0] text-base sm:text-lg leading-relaxed max-w-3xl mx-auto mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Marcus never asked to become the bridge between heaven and damnation. When the sky tears open and angelic forces descend not as saviors but as executioners, he discovers that the seven ancient seals — barriers holding back powers that predate creation itself — are breaking.
          </p>
          <p className="text-[#A0A0A0] text-base sm:text-lg leading-relaxed max-w-3xl mx-auto mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            As each seal shatters, the Nephilim awaken — colossal beings of shadow, fire, and forgotten fury. Heaven's Seraphim hunt them. And beneath it all, something ancient stirs in the deep, turning its attention toward the surface for the first time in millennia.
          </p>
          <p className="text-[#A0A0A0] text-base sm:text-lg leading-relaxed max-w-3xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Joined by Mara, Gideon, the rogue angel Serithiel, and the molten titan Dagon, Marcus must forge an impossible alliance to hold the balance — or witness the unmaking of existence itself.
          </p>
        </div>
      </section>

      {/* ── SHARE & SUPPORT ── */}
      <section data-testid="support-section" className="relative py-20 px-6 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#0A0A0A]/80 backdrop-blur-xl border border-white/10 rounded-lg p-8 text-center hover:border-[#E5C07B]/20 transition-all">
              <Share2 className="w-8 h-8 text-[#E5C07B] mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Share This Film</h3>
              <p className="text-[#A0A0A0] text-sm mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Help others discover The UnMaking</p>
              <div className="flex items-center justify-center gap-3">
                <button data-testid="share-twitter" onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent("Watch The UnMaking — A Dark Fantasy Epic by Christopher Marshall")}&url=${encodeURIComponent(window.location.href)}`, "_blank")} className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#E5C07B]/10 hover:border-[#E5C07B]/30 transition-all"><Twitter className="w-4 h-4 text-[#A0A0A0]" /></button>
                <button data-testid="share-facebook" onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, "_blank")} className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#E5C07B]/10 hover:border-[#E5C07B]/30 transition-all"><Facebook className="w-4 h-4 text-[#A0A0A0]" /></button>
                <button data-testid="share-copy" onClick={handleShare} className="h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center px-4 hover:bg-[#E5C07B]/10 hover:border-[#E5C07B]/30 transition-all text-[#A0A0A0] text-xs tracking-widest uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{copied ? "Copied!" : "Copy Link"}</button>
              </div>
            </div>
            <div className="bg-[#0A0A0A]/80 backdrop-blur-xl border border-white/10 rounded-lg p-8 text-center hover:border-[#FF3D00]/20 transition-all">
              <Heart className="w-8 h-8 text-[#FF3D00] mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Support the Creator</h3>
              <p className="text-[#A0A0A0] text-sm mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Help bring Book 2 to life</p>
              <button data-testid="venmo-tip-button" onClick={() => window.open(`https://venmo.com/${VENMO_USERNAME}`, "_blank")}
                className="inline-flex items-center gap-3 font-bold rounded-sm px-8 py-4 transition-all uppercase tracking-[0.15em] text-sm text-white"
                style={{ fontFamily: "'Outfit', sans-serif", background: "linear-gradient(135deg, #3D95CE, #008CFF)", boxShadow: "0 0 30px rgba(0,140,255,0.3)" }}>
                <ExternalLink className="w-4 h-4" /> Tip via Venmo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── AD ── */}
      <div className="max-w-5xl mx-auto px-6 py-4 relative z-10"><AdZone slot="lower-banner" /></div>

      {/* ── BOOK 2 EMAIL ── */}
      <section data-testid="email-section" className="relative py-24 px-6 z-10">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#FF3D00] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Coming Soon</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif", background: "linear-gradient(180deg, #F5E6C4, #C99C3F)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Book Two Awaits</h2>
          <p className="text-[#A0A0A0] text-sm sm:text-base mb-8 leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>The war for existence is far from over. Be the first to know when the next chapter unfolds.</p>
          {emailStatus !== "success" ? (
            <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input data-testid="email-capture-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" required className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-sm px-5 py-4 text-sm text-[#F5E6C4] placeholder-[#666] focus:outline-none focus:border-[#E5C07B]/50 transition-all" style={{ fontFamily: "'Outfit', sans-serif" }} />
              <button data-testid="email-submit-button" type="submit" disabled={emailStatus === "loading"} className="font-bold rounded-sm px-6 py-4 transition-all uppercase tracking-[0.15em] text-sm whitespace-nowrap disabled:opacity-50" style={{ fontFamily: "'Outfit', sans-serif", background: "linear-gradient(135deg, #E5C07B, #C99C3F)", color: "#050505" }}>
                <Mail className="w-4 h-4 inline mr-2" />{emailStatus === "loading" ? "Sending..." : "Notify Me"}
              </button>
            </form>
          ) : (
            <div data-testid="email-success" className="bg-[#0A0A0A]/80 border border-[#E5C07B]/30 rounded-lg p-6 max-w-md mx-auto">
              <p className="text-[#E5C07B] text-sm tracking-widest uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>You're on the list. The seals are breaking...</p>
            </div>
          )}
          {emailStatus === "error" && <p className="text-[#FF3D00] text-xs mt-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Something went wrong. Please try again.</p>}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer data-testid="footer" className="relative py-16 px-6 z-10 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif", background: "linear-gradient(180deg, #F5E6C4, #C99C3F)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>The UnMaking</p>
          <p className="text-[#666] text-xs tracking-[0.2em] uppercase mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>A Film By Christopher Marshall</p>
          <div className="w-12 h-[1px] bg-white/10 mx-auto mb-6" />
          <p className="text-[#666] text-xs mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Special Thanks to ChatGPT & Emergent</p>
          <p className="text-[#444] text-[10px] tracking-widest uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>All Rights Reserved {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
}
